// /group/:id — group page. Shows:
//   - breadcrumb (待办总览 > 门店管理)
//   - subgroup cards (if any)
//   - 待办 list under this group (and its subs combined)
//
// Click a subgroup card → drill into that subgroup
// Click a 待办 → /task/:id
// Click "新建待办" → opens modal with group_id prefilled

import { useMemo, useState } from 'react';
import { navigate, parseHash } from '../lib/routing.js';
import { Icon } from '../components/Icon.jsx';
import { ProjectRow } from '../components/ProjectRow.jsx';
import { PersonPivotView } from '../components/PersonPivotView.jsx';
import { UNGROUPED_ID, UNGROUPED_GROUP, ungroupedTasks } from '../lib/groups.ts';
import * as api from '../lib/api.ts';

export const GroupDetail = ({ user, tasks, staff, groups, updateTask, addUpdate, onCreate }) => {
  const { parts } = parseHash();
  const groupId = parts[1];
  // The synthetic "未分类" bucket (group-less tasks) has no row in `groups`.
  const isUngrouped = groupId === UNGROUPED_ID;
  const group = isUngrouped ? UNGROUPED_GROUP : groups.find(g => g.id === groupId);

  if (!group) {
    return (
      <>
        <button className="detail-back" onClick={() => navigate('/admin')}>
          <Icon name="back"/> 返回待办总览
        </button>
        <div className="vava-card empty-state">
          <div className="emoji">🔍</div>
          <div className="title">分类不存在</div>
          <button className="btn btn-primary" onClick={() => navigate('/admin')}>返回</button>
        </div>
      </>
    );
  }

  const subgroups = groups
    .filter(g => g.parentId === group.id)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const ancestor = group.parentId ? groups.find(g => g.id === group.parentId) : null;

  // Tasks: this group + all descendants if user is at top-level group
  const groupAndSubIds = useMemo(() => {
    const ids = new Set([group.id]);
    for (const sub of subgroups) ids.add(sub.id);
    return ids;
  }, [group.id, subgroups]);

  // Scope = every task that belongs to this page. For a real group that's the
  // group + its subgroups; for the synthetic 未分类 bucket it's all group-less
  // tasks (groupId null or pointing at a group that no longer exists).
  const tasksInScope = useMemo(() => {
    return isUngrouped
      ? ungroupedTasks(tasks, groups)
      : tasks.filter(t => groupAndSubIds.has(t.groupId));
  }, [isUngrouped, tasks, groups, groupAndSubIds]);

  const mainTasksDirect = isUngrouped
    ? tasksInScope.filter(t => t.parentId === null)
    : tasks.filter(t => t.groupId === group.id && t.parentId === null);
  // Sort by sortOrder (0017+); fall back to createdAt for legacy unset rows.
  const mainTasksAll = useMemo(() => {
    const list = tasksInScope.filter(t => t.parentId === null);
    return [...list].sort((a, b) => {
      const aOrder = a.sortOrder ?? 0;
      const bOrder = b.sortOrder ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.createdAt || '').localeCompare(b.createdAt || '');
    });
  }, [tasksInScope]);

  // Drag-and-drop reorder state. Optimistic update — we mutate the local
  // displayedOrder immediately so the row snaps to position, then persist.
  // If the API rejects we revert. The list re-renders from the sorted tasks
  // once the parent reloads, so we don't need to manage long-term state here.
  const [displayedOrder, setDisplayedOrder] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);

  // View mode: by project (default — recursive tree) or by person (flat
  // pivot). Regina toggles when she wants "what's on Cherry's plate?"
  // rather than "what's the status of the cake-launch project?"
  const [viewMode, setViewMode] = useState('project');

  // Pivot view's task set: every task in scope, regardless of nesting depth
  // (待办 + descendants).
  const pivotTasks = tasksInScope;

  // Orphan subtasks: tasks tagged to THIS group whose parent main task lives
  // elsewhere (or nests below a parent that isn't in scope). The 按项目 tree
  // only renders tasks reachable from an in-scope 待办, so these would
  // silently vanish — the same class of bug as group-less tasks missing from
  // 待办总览. We surface their top-most orphan roots in their own section.
  const orphanRoots = useMemo(() => {
    // BFS from in-scope main tasks over the full graph → every rendered id.
    const rendered = new Set(mainTasksAll.map(t => t.id));
    let added = true;
    while (added) {
      added = false;
      for (const t of tasks) {
        if (t.parentId && rendered.has(t.parentId) && !rendered.has(t.id)) {
          rendered.add(t.id); added = true;
        }
      }
    }
    const orphans = pivotTasks.filter(t => t.parentId !== null && !rendered.has(t.id));
    const orphanIds = new Set(orphans.map(t => t.id));
    // Only the top-most orphans — a nested orphan is shown under its orphan root.
    return orphans.filter(t => !orphanIds.has(t.parentId));
  }, [tasks, pivotTasks, mainTasksAll]);

  // The order to render: server-derived by default, locally-overridden during drag.
  const effectiveTasks = displayedOrder
    ? displayedOrder.map(id => mainTasksAll.find(t => t.id === id)).filter(Boolean)
    : mainTasksAll;

  function handleDragStart(e, id) {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);    // for Firefox compat
  }
  function handleDragOver(e, id) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== overId) setOverId(id);
  }
  function handleDragLeave(e, id) {
    if (id === overId) setOverId(null);
  }
  async function handleDrop(e, targetId) {
    e.preventDefault();
    const sourceId = draggingId || e.dataTransfer.getData('text/plain');
    setDraggingId(null); setOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const ids = effectiveTasks.map(t => t.id);
    const fromIdx = ids.indexOf(sourceId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...ids];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, sourceId);
    setDisplayedOrder(next);                     // optimistic
    try {
      await api.reorderTasks(next);
      // Server re-fetch will eventually sync. For now, keep displayedOrder
      // so the row stays put until tasks state refreshes.
    } catch (err) {
      console.warn('reorder failed:', err.message);
      setDisplayedOrder(null);                   // revert to server order
    }
  }

  return (
    <>
      <button className="detail-back" onClick={() => navigate(ancestor ? '/group/' + ancestor.id : '/admin')}>
        <Icon name="back"/> 返回 {ancestor ? ancestor.name : '待办总览'}
      </button>

      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">
            <span style={{ marginRight: 10 }}>{group.icon || '📁'}</span>{group.name}
          </h1>
          {group.description && (
            <div className="vava-page-meta">{group.description}</div>
          )}
        </div>
        {onCreate && (
          // The 未分类 bucket isn't a real group, so don't prefill it as one —
          // let the user pick a real 分类 in the modal (that's how a task
          // "leaves" 未分类).
          <button className="btn btn-primary"
                  onClick={() => onCreate(isUngrouped ? {} : { groupId: group.id })}>
            <Icon name="plus"/> 新建待办
          </button>
        )}
      </div>

      {subgroups.length > 0 && (
        <section className="group-detail-section">
          <h2 className="group-detail-h2">子分类</h2>
          <div className="group-card-grid">
            {subgroups.map(sg => {
              const subTasks = tasks.filter(t => t.groupId === sg.id);
              const mainCount = subTasks.filter(t => t.parentId === null).length;
              const childCount = subTasks.filter(t => t.parentId !== null).length;
              const doneCount = subTasks.filter(t => t.status === '已完成').length;
              return (
                <button key={sg.id} className="group-card sub"
                        onClick={() => navigate('/group/' + sg.id)}>
                  <div className="group-card-head">
                    <span className="group-card-name">{sg.name}</span>
                  </div>
                  {sg.description && <div className="group-card-desc">{sg.description}</div>}
                  <div className="group-card-meta">
                    <span><strong className="mono">{mainCount}</strong> 待办</span>
                    <span>·</span>
                    <span className="mono">{doneCount}/{childCount + mainCount} 完成</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="group-detail-section">
        <div className="group-detail-section-head">
          <h2 className="group-detail-h2" style={{ margin: 0 }}>
            {viewMode === 'project' ? '待办' : '按人查看'}
            {subgroups.length > 0 && mainTasksAll.length !== mainTasksDirect.length && viewMode === 'project' && (
              <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--vava-text-subtle)', marginLeft: 8 }}>
                ({mainTasksDirect.length} 直属 · {mainTasksAll.length} 含子分类)
              </span>
            )}
            {viewMode === 'person' && (
              <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--vava-text-subtle)', marginLeft: 8 }}>
                ({pivotTasks.length} 条 · 按主负责人分组)
              </span>
            )}
          </h2>
          <div className="vava-segmented" role="tablist" aria-label="切换视图">
            <button type="button"
                    role="tab"
                    aria-selected={viewMode === 'project'}
                    className={viewMode === 'project' ? 'active' : ''}
                    onClick={() => setViewMode('project')}>按项目</button>
            <button type="button"
                    role="tab"
                    aria-selected={viewMode === 'person'}
                    className={viewMode === 'person' ? 'active' : ''}
                    onClick={() => setViewMode('person')}>按人</button>
          </div>
        </div>
        {viewMode === 'person' ? (
          <PersonPivotView tasks={pivotTasks} staff={staff}
                           onOpen={(id) => navigate('/task/' + id)} />
        ) : mainTasksAll.length === 0 && orphanRoots.length === 0 ? (
          <div className="vava-card empty-state">
            <div className="emoji">📋</div>
            <div className="title">还没有待办</div>
            <div className="sub">点击"新建待办"开始</div>
          </div>
        ) : (
          <>
            {mainTasksAll.length > 0 && (
              <div className="project-list">
                {effectiveTasks.map((p, i) => {
                  const subs = tasks.filter(t => t.parentId === p.id);
                  const dragState = draggingId === p.id ? 'dragging'
                                  : overId     === p.id ? 'over'
                                  : null;
                  return (
                    <ProjectRow key={p.id} project={p} subtasks={subs} staff={staff}
                                allTasks={tasks}
                                updateTask={updateTask} addUpdate={addUpdate}
                                onOpen={(id) => navigate('/task/' + id)}
                                defaultOpen={i === 0}
                                draggable
                                dragState={dragState}
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop} />
                  );
                })}
              </div>
            )}
            {orphanRoots.length > 0 && (
              <div className="group-detail-orphans">
                <h3 className="group-detail-h3">
                  来自其他项目的子项
                  <span className="group-detail-orphans-hint">
                    （归在本分类、但待办在别处 · {orphanRoots.length}）
                  </span>
                </h3>
                <div className="project-list">
                  {orphanRoots.map(p => (
                    <ProjectRow key={p.id} project={p}
                                subtasks={tasks.filter(t => t.parentId === p.id)}
                                staff={staff}
                                allTasks={tasks}
                                updateTask={updateTask} addUpdate={addUpdate}
                                onOpen={(id) => navigate('/task/' + id)} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};
